import { Button } from 'components/Button';
import ReturnCustomizationInstructions from './ReturnCustomizationInstructions';
import useReturnCustomizationReview from './useReturnCustomizationReview';
interface Props {
  storeId: string;
  returnId: string;
  orderNumber: string;
  replacementTitle?: string | null;
  onAcknowledged?: () => void;
}
/** Production instruction consent is independent of request and inspection decisions. */
export default function ReturnCustomizationReview(props: Props) {
  const review = useReturnCustomizationReview(props.storeId, props.returnId),
    source = review.source;
  const uuid = (value: string) =>
    /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
      value,
    );
  if (!uuid(props.storeId) || !uuid(props.returnId)) return null;
  if (source?.status === 'NOT_REQUIRED' && !review.error && !review.readError)
    return null;
  if (
    !source ||
    source.status === 'UNAVAILABLE' ||
    !source.intentId ||
    !source.intentDigest ||
    !source.replacementCustomization ||
    !source.quantity ||
    !source.replacementVariantId
  )
    return (
      <section
        aria-label="Replacement production instructions"
        className="mb-6 space-y-3 rounded-xl border border-gray-200 bg-white p-4"
      >
        <h3 className="text-sm font-semibold text-gray-900">
          Replacement instructions
        </h3>
        {review.loading ? (
          <p role="status" className="text-sm text-gray-600">
            Checking replacement instructions…
          </p>
        ) : (
          <>
            <p role="alert" className="text-sm text-amber-800">
              Replacement instructions could not be verified. Check the latest
              instructions before processing.
            </p>
            <Button
              variant="outlined"
              disabled={review.pending}
              onClick={review.reload}
            >
              Check latest instructions
            </Button>
          </>
        )}
      </section>
    );
  return (
    <div className="mb-6 space-y-3">
      {source.status === 'REVIEW_REQUIRED' && (
        <p
          role="alert"
          className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900"
        >
          This customized replacement needs a team review before processing.
          Instruction acknowledgment does not clear other required checks.
        </p>
      )}
      {(review.error || review.readError) && (
        <p role="alert" className="text-sm text-amber-800">
          {review.error ||
            'The latest instructions could not be loaded. Check the latest instructions before processing.'}
        </p>
      )}
      <ReturnCustomizationInstructions
        instructionState={
          source.status === 'PREPARING_INSTRUCTIONS'
            ? 'PREPARING'
            : source.status === 'REVIEW_REQUIRED'
            ? 'REVIEW_REQUIRED'
            : 'VERIFIED'
        }
        scopeKey={JSON.stringify([props.storeId, props.returnId])}
        instructions={{
          intentId: source.intentId,
          digest: source.intentDigest,
          orderNumber: props.orderNumber,
          replacementTitle: props.replacementTitle || 'Replacement garment',
          variantLabel: [
            ...source.selectedOptions.map(
              (option) => `${option.name}: ${option.value}`,
            ),
            source.replacementVariantId,
          ].join(' · '),
          quantity: source.quantity,
          heightLabel: source.replacementCustomization.heightLabel,
          heightValueLabel: source.replacementCustomization.heightValueLabel,
        }}
        canAcknowledge={review.canAcknowledge}
        acknowledgedDigest={source.acknowledgment?.intentDigest || null}
        pending={review.pending || review.loading}
        onAcknowledge={async (input) => {
          const accepted = await review.acknowledge(input);
          if (accepted) props.onAcknowledged?.();
          return accepted;
        }}
        onReload={review.reload}
      />
    </div>
  );
}
